import {
  AlertTriangle,
  CreditCard,
  FileText,
  Layers,
  Plus,
  RefreshCw,
  Server,
  ShoppingCart,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import type { HeaderMode, HeaderQuickActionItem, HeaderSearchItem } from '@/types/header';

export interface HeaderQuickActionConfig extends HeaderQuickActionItem {
  icon: LucideIcon;
}

export const APP_HEADER_SEARCH_ITEMS: HeaderSearchItem[] = [
  {
    id: 'dashboard',
    path: '/dashboard',
    label: { namespace: 'navigation', key: 'dashboard' },
    group: 'core',
    keywords: ['home', 'kpi', 'tableau'],
  },
  {
    id: 'sales',
    path: '/sales',
    label: { namespace: 'navigation', key: 'sales' },
    group: 'commerce',
    keywords: ['commande', 'caisse', 'vente'],
  },
  {
    id: 'sales-statistics',
    path: '/sales/statistics',
    label: { namespace: 'navigation', key: 'statistics' },
    group: 'analytics',
    keywords: ['rapport', 'performance', 'revenu'],
  },
  {
    id: 'products',
    path: '/products',
    label: { namespace: 'navigation', key: 'products' },
    group: 'commerce',
    keywords: ['catalogue', 'article'],
  },
  {
    id: 'categories',
    path: '/categories',
    label: { namespace: 'navigation', key: 'categories' },
    group: 'commerce',
    keywords: ['classification'],
  },
  {
    id: 'brands',
    path: '/brands',
    label: { namespace: 'navigation', key: 'brands' },
    group: 'commerce',
    keywords: ['marque'],
  },
  {
    id: 'stock',
    path: '/stock',
    label: { namespace: 'navigation', key: 'stock' },
    group: 'commerce',
    keywords: ['inventaire'],
  },
  {
    id: 'stock-alerts',
    path: '/stock/alerts',
    label: { namespace: 'navigation', key: 'alerts' },
    group: 'commerce',
    keywords: ['rupture', 'seuil'],
  },
  {
    id: 'customers',
    path: '/customers',
    label: { namespace: 'navigation', key: 'customers' },
    group: 'commerce',
    keywords: ['crm', 'client'],
  },
  {
    id: 'employees',
    path: '/employees',
    label: { namespace: 'navigation', key: 'employees' },
    group: 'core',
    keywords: ['staff', 'team', 'equipe'],
  },
  {
    id: 'subscription',
    path: '/subscription',
    label: { namespace: 'navigation', key: 'subscription' },
    group: 'settings',
    keywords: ['plan', 'billing', 'paiement'],
  },
  {
    id: 'notifications',
    path: '/settings/notifications',
    label: { namespace: 'navigation', key: 'notifications' },
    group: 'settings',
    keywords: ['telegram', 'alertes', 'alerts', 'bell'],
  },
  {
    id: 'preferences',
    path: '/settings/preferences',
    label: { namespace: 'navigation', key: 'preferences' },
    group: 'settings',
    keywords: ['langue', 'theme'],
  },
  {
    id: 'profile',
    path: '/settings/profile',
    label: { namespace: 'navigation', key: 'profile' },
    group: 'settings',
    keywords: ['compte', 'user'],
  },
  {
    id: 'support',
    path: '/support',
    label: { namespace: 'navigation', key: 'support' },
    group: 'system',
    keywords: ['help', 'aide', 'assistance'],
  },
  {
    id: 'guide',
    path: '/guide',
    label: { namespace: 'navigation', key: 'guide' },
    group: 'system',
    keywords: ['guide', 'help', 'aide', 'tutorial', 'tutoriel', 'documentation'],
  },
];

export const ADMIN_HEADER_SEARCH_ITEMS: HeaderSearchItem[] = [
  {
    id: 'admin-dashboard',
    path: '/admin',
    label: { namespace: 'admin', key: 'admin_dashboard' },
    group: 'core',
    keywords: ['overview', 'pilotage'],
  },
  {
    id: 'admin-users',
    path: '/users',
    label: { namespace: 'admin', key: 'users' },
    group: 'core',
    keywords: ['utilisateurs', 'accounts'],
  },
  {
    id: 'admin-shops',
    path: '/shops',
    label: { namespace: 'admin', key: 'shops' },
    group: 'commerce',
    keywords: ['tenants', 'boutiques'],
  },
  {
    id: 'admin-subscriptions',
    path: '/subscriptions',
    label: { namespace: 'admin', key: 'subscriptions' },
    group: 'commerce',
    keywords: ['plans', 'billing'],
  },
  {
    id: 'admin-quotes',
    path: '/quotes',
    label: { namespace: 'admin', key: 'quote_requests' },
    group: 'commerce',
    keywords: ['devis', 'pending'],
  },
  {
    id: 'admin-plans',
    path: '/plans',
    label: { namespace: 'admin', key: 'plans' },
    group: 'commerce',
    keywords: ['pricing', 'offres'],
  },
  {
    id: 'admin-finance',
    path: '/finance',
    label: { namespace: 'admin', key: 'finance' },
    group: 'analytics',
    keywords: ['revenu', 'transactions'],
  },
  {
    id: 'admin-sync',
    path: '/sync',
    label: { namespace: 'admin', key: 'sync_monitoring' },
    group: 'system',
    keywords: ['sync', 'offline', 'device'],
  },
  {
    id: 'admin-logs',
    path: '/system/logs',
    label: { namespace: 'admin', key: 'audit_logs' },
    group: 'system',
    keywords: ['logs', 'security'],
  },
  {
    id: 'admin-profile',
    path: '/profile',
    label: { namespace: 'admin', key: 'my_profile' },
    group: 'settings',
    keywords: ['profile', 'compte'],
  },
];

export const APP_HEADER_QUICK_ACTIONS: HeaderQuickActionConfig[] = [
  {
    id: 'new-sale',
    path: '/pos',
    icon: ShoppingCart,
    label: { namespace: 'common', key: 'header_quick_sale' },
    description: { namespace: 'common', key: 'header_quick_sale_desc' },
    priority: 'primary',
  },
  {
    id: 'new-product',
    path: '/products/create',
    icon: Plus,
    label: { namespace: 'common', key: 'header_quick_product' },
    description: { namespace: 'common', key: 'header_quick_product_desc' },
    priority: 'secondary',
  },
  {
    id: 'new-customer',
    path: '/customers',
    icon: UserPlus,
    label: { namespace: 'common', key: 'header_quick_customer' },
    description: { namespace: 'common', key: 'header_quick_customer_desc' },
    priority: 'secondary',
  },
  {
    id: 'stock-alerts',
    path: '/stock/alerts',
    icon: AlertTriangle,
    label: { namespace: 'common', key: 'header_quick_stock_alerts' },
    description: { namespace: 'common', key: 'header_quick_stock_alerts_desc' },
    priority: 'secondary',
  },
];

export const ADMIN_HEADER_QUICK_ACTIONS: HeaderQuickActionConfig[] = [
  {
    id: 'review-quotes',
    path: '/quotes?status=pending',
    icon: FileText,
    label: { namespace: 'common', key: 'header_quick_review_quotes' },
    description: { namespace: 'common', key: 'header_quick_review_quotes_desc' },
    priority: 'primary',
  },
  {
    id: 'review-subscriptions',
    path: '/subscriptions',
    icon: CreditCard,
    label: { namespace: 'common', key: 'header_quick_review_subscriptions' },
    description: { namespace: 'common', key: 'header_quick_review_subscriptions_desc' },
    priority: 'secondary',
  },
  {
    id: 'new-plan',
    path: '/plans/create',
    icon: Layers,
    label: { namespace: 'common', key: 'header_quick_new_plan' },
    description: { namespace: 'common', key: 'header_quick_new_plan_desc' },
    priority: 'secondary',
  },
  {
    id: 'sync-monitoring',
    path: '/sync',
    icon: RefreshCw,
    label: { namespace: 'common', key: 'header_quick_sync' },
    description: { namespace: 'common', key: 'header_quick_sync_desc' },
    priority: 'secondary',
  },
  {
    id: 'audit-logs',
    path: '/system/logs',
    icon: Server,
    label: { namespace: 'common', key: 'header_quick_audit_logs' },
    description: { namespace: 'common', key: 'header_quick_audit_logs_desc' },
    priority: 'secondary',
  },
];

export function getHeaderSearchItems(mode: HeaderMode): HeaderSearchItem[] {
  return mode === 'admin' ? ADMIN_HEADER_SEARCH_ITEMS : APP_HEADER_SEARCH_ITEMS;
}

export function getQuickActions(mode: HeaderMode): HeaderQuickActionConfig[] {
  return mode === 'admin' ? ADMIN_HEADER_QUICK_ACTIONS : APP_HEADER_QUICK_ACTIONS;
}

export function getPrimaryQuickAction(mode: HeaderMode): HeaderQuickActionConfig | null {
  const actions = getQuickActions(mode);
  return actions.find((action) => action.priority === 'primary') ?? actions[0] ?? null;
}

export function getSecondaryQuickActions(mode: HeaderMode): HeaderQuickActionConfig[] {
  return getQuickActions(mode).filter((action) => action.priority !== 'primary');
}

export function matchRouteByPathname(pathname: string, mode: HeaderMode): HeaderSearchItem | null {
  const items = getHeaderSearchItems(mode);
  const sorted = [...items].sort((a, b) => b.path.length - a.path.length);

  for (const item of sorted) {
    if (item.path === '/admin') {
      if (pathname === '/admin') return item;
      continue;
    }

    if (pathname === item.path || pathname.startsWith(`${item.path}/`)) {
      return item;
    }
  }

  return null;
}
